import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import { GET_TEST, GET_TESTS, GET_STATS, DELETE_TEST, UPDATE_TEST, GET_ENTITY_HISTORY, GET_SOURCES, LINK_SOURCE_TO_TEST, UNLINK_SOURCE_FROM_TEST, GET_JIRA_STATUS } from "@/graphql/queries";
import { mapToSnakeCase } from "@/graphql/mappers";
import { useApp } from "@/context/AppContext";
import { Badge } from "@/components/Badge";
import { useState } from "react";
import type { Test, Source } from "@/types";

export function TestDetail() {
  const { activeId, navigateTo, activeUser } = useApp();
  const client = useApolloClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<Partial<Test>>({});

  const { data: testRaw, loading: isLoading } = useQuery<any>(GET_TEST, {
    variables: { id: activeId! },
    skip: !activeId,
  });

  const { data: sourcesRaw } = useQuery<any>(GET_SOURCES);

  const { data: jiraStatusRaw } = useQuery<any>(GET_JIRA_STATUS);
  const jiraBaseUrl = jiraStatusRaw?.jiraStatus?.baseUrl || "";

  const { data: historyRaw } = useQuery<any>(GET_ENTITY_HISTORY, {
    variables: { entityType: "test", entityId: activeId!, limit: 20 },
    skip: !activeId,
  });

  const test = testRaw ? mapToSnakeCase(testRaw.test) : undefined;
  const allSources: Source[] = sourcesRaw ? sourcesRaw.sources : [];

  const [deleteMutation] = useMutation<any>(DELETE_TEST, {
    onCompleted: async () => {
      await client.refetchQueries({ include: [GET_TESTS, GET_STATS] });
      navigateTo("tests");
    },
  });

  const [updateMutation] = useMutation<any>(UPDATE_TEST, {
    onCompleted: () => {
      client.refetchQueries({ include: [GET_TEST, GET_TESTS] });
      setEditing(false);
    },
  });

  const [linkSourceMutation] = useMutation<any>(LINK_SOURCE_TO_TEST, {
    onCompleted: () => { client.refetchQueries({ include: [GET_TEST] }); },
  });
  const [unlinkSourceMutation] = useMutation<any>(UNLINK_SOURCE_FROM_TEST, {
    onCompleted: () => { client.refetchQueries({ include: [GET_TEST] }); },
  });

  if (!activeId) return null;
  if (isLoading) return <div className="p-5 text-text2">Loading...</div>;
  if (!test) return <div className="p-5 text-text2">Not found</div>;

  const canEdit = activeUser?.role === "manager" || activeUser?.role === "admin" || activeUser?.role === "lead";
  const displayedTestStatus = test.type === "jira-tracked" ? test.jira_status || "unknown" : test.test_status;

  function startEdit() {
    setEditData({ title: test!.title || "", description: test!.description || "", type: test!.type || "", suite: test!.suite || "", test_status: test!.test_status || "unknown" });
    setEditing(true);
  }

  function saveEdit() {
    updateMutation({
      variables: {
        id: activeId!,
        input: {
          title: editData.title,
          description: editData.description,
          type: editData.type,
          suite: editData.suite,
          testStatus: editData.test_status,
        },
        userId: activeUser!.id,
      },
    });
  }

  return (
    <div data-testid="test-detail">
      {/* ID */}
      <div className="mb-5 text-[11px] text-accent font-semibold uppercase tracking-[0.3px]">{test.id}</div>

      {/* Badges + actions */}
      <div className="mb-5 flex items-center justify-between">
        <div className="flex gap-1.5 flex-wrap">
          {test.type && <Badge>{test.type}</Badge>}
          {test.suite && <Badge variant="info">{test.suite}</Badge>}
          {test.jira_status && (
            <Badge variant={test.all_done ? "success" : "warning"}>{test.jira_status}</Badge>
          )}
          {test.has_issues && <Badge variant="danger">Issues</Badge>}
        </div>
        {canEdit && !editing && (
          <div className="flex gap-1.5">
            <button
              className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-accent hover:text-text hover:bg-bg3"
              onClick={startEdit}
            >
              Edit
            </button>
            <button
              className="px-[10px] py-[5px] border border-red/40 rounded-default bg-transparent text-red text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-red hover:bg-red/10"
              aria-label="Delete test"
              onClick={() => setDeleteConfirm(true)}
            >
              Delete
            </button>
          </div>
        )}
      </div>
      {deleteConfirm && (
        <div className="mb-5 p-3 border border-red/30 rounded-default bg-red/5">
          <p className="text-xs text-red font-semibold mb-1">Are you sure you want to delete {test.id}?</p>
          <p className="text-xs text-text2">This action cannot be undone. The test and all its requirement links will be permanently removed.</p>
          <div className="flex gap-2 mt-2">
            <button
              className="px-[10px] py-[5px] border border-red rounded-default bg-red text-white text-xs cursor-pointer font-semibold transition-colors hover:bg-red/80"
              onClick={() => {
                deleteMutation({ variables: { id: test.id, userId: activeUser!.id } });
                setDeleteConfirm(false);
              }}
            >
              Yes, delete permanently
            </button>
            <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer transition-colors hover:border-accent hover:text-text" onClick={() => setDeleteConfirm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Description */}
      <div className="mb-5">
        <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Description</h3>
        {editing ? (
          <textarea
            value={editData.description || ""}
            onChange={e => setEditData({ ...editData, description: e.target.value })}
            className="w-full bg-bg border border-border rounded-default px-3 py-2 text-text text-xs min-h-[80px] resize-y"
          />
        ) : (
          <p className="text-[13px] text-text leading-[1.6]">{test.description || <span className="text-text2 italic">No description</span>}</p>
        )}
      </div>

      {/* Properties */}
      {!editing && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Properties</h3>
          <div className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-text2">Title</dt><dd className="text-text">{test.title || "—"}</dd>
            <dt className="text-text2">Type</dt><dd className="text-text">{test.type || "—"}</dd>
            <dt className="text-text2">Suite</dt><dd className="text-text">{test.suite || "—"}</dd>
            <dt className="text-text2">Test Status</dt><dd className="text-text">{displayedTestStatus || "—"}</dd>
            <dt className="text-text2">Execution Sources</dt><dd className="text-text"><SourcesList sources={test.sources} /></dd>
          </div>
        </div>
      )}

      {/* Sources management */}
      {canEdit && !editing && (
        <div className="mb-5">
          <SourceSelector
            currentSources={test.sources || []}
            allSources={allSources}
            onLink={(sourceId) => linkSourceMutation({ variables: { sourceId, testId: test.id } })}
            onUnlink={(sourceId) => unlinkSourceMutation({ variables: { sourceId, testId: test.id } })}
          />
        </div>
      )}

      {/* Edit controls */}
      {editing && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Edit Fields</h3>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-text2 w-20">Title</label>
              <input type="text" value={editData.title || ""} onChange={e => setEditData({ ...editData, title: e.target.value })} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-text2 w-20">Type</label>
              <select value={editData.type || ""} onChange={e => setEditData({ ...editData, type: e.target.value })} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs">
                <option value="">None</option>
                <option value="checklist">Checklist</option>
                <option value="jira-tracked">Jira-tracked</option>
                <option value="pipeline">Pipeline</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-[11px] text-text2 w-20">Suite</label>
              <input type="text" value={editData.suite || ""} onChange={e => setEditData({ ...editData, suite: e.target.value })} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs" />
            </div>
            {editData.type && editData.type !== "jira-tracked" && (
              <div className="flex items-center gap-2">
                <label className="text-[11px] text-text2 w-20">Status</label>
                <select value={editData.test_status || "unknown"} onChange={e => setEditData({ ...editData, test_status: e.target.value })} className="flex-1 bg-bg border border-border rounded-default px-2 py-1 text-text text-xs">
                  <option value="done">Done</option>
                  <option value="not_done">Not done</option>
                  <option value="pending">Pending</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <button className="px-[10px] py-[5px] border border-accent rounded-default bg-accent text-white text-xs cursor-pointer flex items-center gap-1 transition-colors font-semibold hover:bg-accent2 hover:border-accent2" onClick={saveEdit}>Save</button>
            <button className="px-[10px] py-[5px] border border-border rounded-default bg-transparent text-text2 text-xs cursor-pointer flex items-center gap-1 transition-colors hover:border-accent hover:text-text hover:bg-bg3" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Issues */}
      {test.issues && test.issues.length > 0 && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Issues</h3>
          {test.issues.map((issue: string, i: number) => (
            <div key={i} className="text-xs text-red flex gap-1.5 mb-1">
              <span>⚠</span><span>{issue}</span>
            </div>
          ))}
        </div>
      )}

      {/* Jira tickets */}
      {test.jira_tickets && test.jira_tickets.length > 0 && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Jira Tickets</h3>
          <div className="flex flex-wrap gap-1">
            {test.jira_tickets.map((ticket: string) => (
              jiraBaseUrl ? (
                <a key={ticket} href={`${jiraBaseUrl}/browse/${ticket}`} target="_blank" rel="noopener noreferrer">
                  <Badge variant="muted" className="cursor-pointer hover:opacity-80">{ticket}</Badge>
                </a>
              ) : (
                <Badge key={ticket} variant="muted">{ticket}</Badge>
              )
            ))}
          </div>
        </div>
      )}

      {/* Linked requirements */}
      {test.requirements && test.requirements.length > 0 && (
        <div className="mb-5">
          <h3 className="text-[11px] text-text2 uppercase tracking-[0.5px] mb-1.5">Linked Requirements</h3>
          {test.requirements.map((reqId: string) => (
            <div key={reqId} className="px-2.5 py-1.5 text-[13px] cursor-pointer border-b border-border transition-colors duration-150 hover:bg-bg3" onClick={() => navigateTo("requirements", reqId)}>
              <span className="text-accent font-semibold">{reqId}</span>
            </div>
          ))}
        </div>
      )}

      {/* History */}
      <div className="border-t border-border pt-3">
        <button
          className="text-[11px] text-text2 uppercase tracking-[0.5px] cursor-pointer bg-transparent border-none hover:text-accent transition-colors"
          onClick={() => setHistoryOpen(!historyOpen)}
        >
          {historyOpen ? "▾" : "▸"} History
        </button>
        {historyOpen && (
          <HistorySection entries={historyRaw ? mapToSnakeCase(historyRaw.entityHistory) : []} />
        )}
      </div>
    </div>
  );
}

interface HistoryEntry {
  id: number;
  ts: string;
  user_id: string;
  action: string;
  changed_fields: string | null;
}

function SourcesList({ sources }: { sources?: Source[] }) {
  if (!sources || sources.length === 0) return <span>—</span>;
  return (
    <div className="flex flex-col gap-0.5" data-testid="sources-list">
      {sources.map((s) =>
        s.url ? (
          <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline break-all">{s.label}</a>
        ) : (
          <span key={s.id} className="break-all">{s.label}</span>
        )
      )}
    </div>
  );
}

function SourceSelector({ currentSources, allSources, onLink, onUnlink }: { currentSources: Source[]; allSources: Source[]; onLink: (id: string) => void; onUnlink: (id: string) => void }) {
  const linkedIds = new Set(currentSources.map(s => s.id));
  const available = allSources.filter(s => !linkedIds.has(s.id));
  return (
    <div className="flex flex-col gap-1" data-testid="source-selector">
      <label className="text-[11px] text-text2">Execution Sources</label>
      {currentSources.map(s => (
        <div key={s.id} className="flex items-center gap-1">
          <span className="text-xs text-text flex-1">{s.label}</span>
          <button type="button" className="text-red text-xs bg-transparent border-none cursor-pointer px-1" onClick={() => onUnlink(s.id)} aria-label={`Remove source ${s.label}`}>x</button>
        </div>
      ))}
      {available.length > 0 && (
        <select
          className="bg-bg border border-border rounded-default px-2 py-1 text-text text-xs"
          value=""
          onChange={e => { if (e.target.value) onLink(e.target.value); }}
        >
          <option value="">+ Link execution source…</option>
          {available.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      )}
    </div>
  );
}

function HistorySection({ entries }: { entries: HistoryEntry[] }) {
  if (entries.length === 0) {
    return <div className="text-xs text-text2 py-2 mt-2">No history recorded.</div>;
  }
  return (
    <div className="mt-2">
      {entries.map(e => {
        const changes = e.changed_fields ? JSON.parse(e.changed_fields) : null;
        return (
          <div key={e.id} className="text-xs border-b border-border/50 py-1.5">
            <div className="flex items-center gap-2">
              <span className="text-text2">{formatTs(e.ts)}</span>
              <span className="text-text">{e.user_id}</span>
              <span className="text-accent">{e.action}</span>
            </div>
            {changes && (
              <div className="mt-0.5 pl-4 text-text2">
                {Object.entries(changes).map(([field, val]: [string, any]) => (
                  <div key={field}>
                    <span className="text-text">{field}</span>: {String(val.old)} → {String(val.new)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
